/**
 * Sender-display helpers for the Review UI.
 *
 * Ingestion persists the RAW `From` header verbatim in `emails.from_email`
 * (webhooks-gmail-watch stores `getHeader(headers, 'From')` as-is), so the
 * column is frequently
 * `"Principal Skinner" <principal@school.edu>` — a display name plus an
 * angle-bracketed address. The Review cards should surface just the human
 * name per Figma (YAR-131), so this parses the header and falls back to the
 * bare address when no name is present.
 */

/**
 * Extract a human-friendly sender name from a stored `from_email` value.
 *
 * - `"Principal Skinner" <principal@school.edu>` → `Principal Skinner`
 * - `Principal Skinner <principal@school.edu>`   → `Principal Skinner`
 * - `<principal@school.edu>`                     → `principal@school.edu`
 * - `principal@school.edu`                       → `principal@school.edu`
 *
 * The raw value is kept untouched for search/matching; this is display-only.
 */
export function senderDisplayName(fromEmail: string): string {
  const raw = (fromEmail ?? '').trim();
  if (!raw) return '';

  // `Display Name <addr@host>` (name optionally wrapped in double quotes).
  const match = raw.match(/^(.*?)<([^>]+)>\s*$/);
  if (match) {
    const name = match[1]
      .trim()
      .replace(/^"(.*)"$/, '$1')
      .trim();
    return name || match[2].trim();
  }

  // Bare address, or a value that is already just a name — show as-is.
  return raw;
}

/**
 * What Review needs to know about a forwarded email.
 *
 * `forwardedBy` is absent on anything ingested before that field shipped, and
 * it stays absent forever for those rows — the outer `From` was never stored
 * and cannot be recovered, since `from_email` was overwritten with the inner
 * sender at insert time. So the two flags are deliberately independent:
 * "this was forwarded" is knowable for every row, "who forwarded it" is not.
 */
export interface ForwardProvenance {
  isForwarded: boolean;
  forwardedBy: string | null;
}

/**
 * Read forwarding provenance out of an email's `preprocessing_metadata`.
 *
 * Defensive about shape on purpose: the column is jsonb with a passthrough
 * schema, it is written by a different service, and rows predate several of
 * its fields. Anything unexpected reads as "not forwarded", which renders
 * exactly what Review rendered before this existed.
 */
export function readForwardProvenance(metadata: unknown): ForwardProvenance {
  const none: ForwardProvenance = { isForwarded: false, forwardedBy: null };
  if (typeof metadata !== 'object' || metadata === null) return none;

  const unwrap = (metadata as { forward_unwrap?: unknown }).forward_unwrap;
  if (typeof unwrap !== 'object' || unwrap === null) return none;

  const { is_forwarded: isForwarded, forwarded_by: forwardedBy } = unwrap as {
    is_forwarded?: unknown;
    forwarded_by?: unknown;
  };
  if (isForwarded !== true) return none;

  return {
    isForwarded: true,
    forwardedBy:
      typeof forwardedBy === 'string' && forwardedBy.trim()
        ? forwardedBy
        : null,
  };
}

/**
 * The organisation behind a shared sending platform, off `emails.sender_match`.
 *
 * Ingestion writes it from Reply-To/List-Id and the service layer strips the
 * column down to this one field, so anything else that was in there never
 * reaches the client. Null for ordinary senders, for platforms that gave us
 * nothing to go on, and for every email stored before this shipped — the
 * headers it is derived from were never kept, so old rows cannot be backfilled.
 */
export function readSenderOrg(senderMatch: unknown): string | null {
  if (typeof senderMatch !== 'object' || senderMatch === null) return null;
  const org = (senderMatch as { sender_org?: unknown }).sender_org;
  return typeof org === 'string' && org.trim() ? org : null;
}

/**
 * The sender context line for Review (`via …` under the sender).
 *
 * `emails.sender_label` is computed on the server (YAR-477): the organisation
 * resolved at ingestion, else the From display name, never an address. It is
 * the only form of the organisation the client can read — `sender_match` is
 * not granted to `authenticated`, so the list and detail services stub it
 * null and `readSenderOrg` alone always came back empty. When the label is
 * just the display name, `SourceInfo` suppresses it as a repeat.
 */
export function senderContextLabel(email: {
  sender_label?: string | null;
  sender_match?: unknown;
}): string | null {
  const label = email.sender_label?.trim();
  if (label && !label.includes('@')) return label;
  return readSenderOrg(email.sender_match);
}
